import React, { useEffect, useState } from 'react';
import { PlusIcon, XIcon, ChevronUpIcon, ChevronDownIcon, HelpCircleIcon, MoreVerticalIcon, EditIcon, PauseIcon, PlayIcon, TrashIcon } from 'lucide-react';
interface KeywordsTabProps {
  brandId: number;
}
type SortField = 'keyword' | 'source' | 'status';
type SortDirection = 'asc' | 'desc';
export const KeywordsTab: React.FC<KeywordsTabProps> = ({
  brandId
}) => {
  const [sortField, setSortField] = useState<SortField>('keyword');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  // Mock keywords data
  const [keywords, setKeywords] = useState([{
    id: 1,
    keyword: 'acme',
    source: 'auto',
    status: 'active'
  }, {
    id: 2,
    keyword: 'acmeinc',
    source: 'auto',
    status: 'active'
  }, {
    id: 3,
    keyword: 'acme-inc',
    source: 'manual',
    status: 'active'
  }, {
    id: 4,
    keyword: 'acmecompany',
    source: 'manual',
    status: 'paused'
  }, {
    id: 5,
    keyword: 'acmecorp',
    source: 'auto',
    status: 'active'
  }, {
    id: 6,
    keyword: 'acmegroup',
    source: 'manual',
    status: 'active'
  }, {
    id: 7,
    keyword: 'acmesupport',
    source: 'manual',
    status: 'active'
  }, {
    id: 8,
    keyword: 'acmeofficial',
    source: 'auto',
    status: 'active'
  }, {
    id: 9,
    keyword: 'acmestore',
    source: 'manual',
    status: 'paused'
  }, {
    id: 10,
    keyword: 'acmeportal',
    source: 'manual',
    status: 'active'
  }]);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    const sortedKeywords = [...keywords].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];
      const sortOrder = sortDirection === 'asc' ? 1 : -1;
      return aValue.localeCompare(bValue) * sortOrder;
    });
    setKeywords(sortedKeywords);
  };
  const handleMenuClick = (keywordId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === keywordId ? null : keywordId);
  };
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);
  const SortHeader: React.FC<{
    field: SortField;
    label: string;
  }> = ({
    field,
    label
  }) => <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort(field)} data-id="element-160">
      <div className="flex items-center space-x-1" data-id="element-161">
        <span data-id="element-162">{label}</span>
        <div className="flex flex-col" data-id="element-163">
          <ChevronUpIcon className={`h-3 w-3 ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-164" />
          <ChevronDownIcon className={`h-3 w-3 ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-165" />
        </div>
      </div>
    </th>;
  return <div className="space-y-6" data-id="element-166">
      <div className="bg-white shadow-sm rounded-lg p-6" data-id="element-167">
        <div className="flex items-center justify-between mb-6" data-id="element-168">
          <div className="flex items-center" data-id="element-169">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-170">Keywords</h2>
            <div className="relative ml-2 group" data-id="element-171">
              <HelpCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-500 cursor-help" data-id="element-172" />
              <div className="hidden group-hover:block absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10" data-id="element-173">
                Keywords are used to scan for potential phishing domains. We
                automatically generate keywords based on your brand name, but
                you can also add custom keywords.
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-900 rotate-45" data-id="element-174"></div>
              </div>
            </div>
          </div>
          <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700" data-id="element-175">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-176" />
            Add Keyword
          </button>
        </div>
        <div className="overflow-x-auto" data-id="element-177">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-178">
            <thead className="bg-gray-50" data-id="element-179">
              <tr data-id="element-180">
                <SortHeader field="keyword" label="Keyword" data-id="element-181" />
                <SortHeader field="source" label="Source" data-id="element-182" />
                <SortHeader field="status" label="Status" data-id="element-183" />
                <th scope="col" className="hidden md:table-cell px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-184">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-185">
              {keywords.map(keyword => <tr key={keyword.id} className="hover:bg-gray-50" data-id="element-186">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-187">
                    {keyword.keyword}
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-188">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${keyword.source === 'auto' ? 'bg-gray-100 text-gray-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-189">
                      {keyword.source.charAt(0).toUpperCase() + keyword.source.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-190">
                    <div className="flex items-center" data-id="element-191">
                      <div className={`h-2.5 w-2.5 rounded-full mr-2 ${keyword.status === 'active' ? 'bg-green-400' : 'bg-gray-400'}`} data-id="element-192"></div>
                      {keyword.status.charAt(0).toUpperCase() + keyword.status.slice(1)}
                    </div>
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-193">
                    <div className="flex justify-end" data-id="element-194">
                      <div className="relative" data-id="element-195">
                        <button onClick={e => handleMenuClick(keyword.id, e)} className="text-gray-400 hover:text-gray-500 p-1 rounded-full hover:bg-gray-100" data-id="element-196">
                          <MoreVerticalIcon className="h-4 w-4" data-id="element-197" />
                        </button>
                        {openMenuId === keyword.id && <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10" data-id="element-198">
                            <div className="py-1" role="menu" data-id="element-199">
                              <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-200">
                                <EditIcon className="h-4 w-4 mr-3" data-id="element-201" />
                                Edit
                              </button>
                              <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem" data-id="element-202">
                                {keyword.status === 'active' ? <>
                                    <PauseIcon className="h-4 w-4 mr-3" data-id="element-203" />
                                    Pause
                                  </> : <>
                                    <PlayIcon className="h-4 w-4 mr-3" data-id="element-204" />
                                    Resume
                                  </>}
                              </button>
                              <button className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-gray-100" role="menuitem" data-id="element-205">
                                <TrashIcon className="h-4 w-4 mr-3" data-id="element-206" />
                                Delete
                              </button>
                            </div>
                          </div>}
                      </div>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};